// src/components/AudioMeter/AudioMeter.tsx
import React, { useState, useEffect, useRef } from 'react';
import "./AudioMeter.css"

interface AudioMeterProps {
  onDbChange: (db: number) => void;
}

export function AudioMeter({ onDbChange }: AudioMeterProps): JSX.Element {
  const [offset, setOffset] = useState<number>(0);
  const localDbValues = useRef<number[]>([]);
  const refreshRate = useRef<number>(500);

  useEffect(() => {
    let context: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;

    const startAudioProcessing = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        context = new AudioContext();
        const source = context.createMediaStreamSource(stream);
        const processor = context.createScriptProcessor(2048, 1, 1);
        analyser = context.createAnalyser();

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 256;

        source.connect(analyser);
        analyser.connect(processor);
        processor.connect(context.destination);

        processor.onaudioprocess = () => {
          if (analyser) {
            const data = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(data);
            let rms = 0;

            for (let i = 0; i < data.length; i++) {
              if (data[i] > 120) data[i] = 120;
              rms += data[i] * data[i];
            }
            rms = Math.sqrt(rms / data.length);
            const value = rms + offset;
            localDbValues.current.push(value);
          }
        };      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    };

    startAudioProcessing();

    const updateDb = () => {
      const volume = Math.round(
        localDbValues.current.reduce((a, b) => a + b, 0) / localDbValues.current.length || 0
      );
      localDbValues.current = [];
      onDbChange(volume);
    };

    const interval = setInterval(updateDb, refreshRate.current);
    return () => {
      clearInterval(interval);
      context?.close();
    };
  }, [offset, onDbChange]);

  return (
    <div className='AudioMeter'>
      <input
        type="range"
        min="-20"
        max="20"
        value={offset}
        onChange={(e) => setOffset(parseInt(e.target.value))}
        id="offset"
      />
      <span id="offset_value">{offset}</span>
    </div>
  );
}

export default AudioMeter;
